import { createSlice } from "@reduxjs/toolkit";
import { productApi } from "../../api/productApi";
import { setLoading, setToast } from "../../app/appSlice";

export const sDActions = Object.freeze({
  updateProduct: "Update ProductPopup",
  viewSD: "View Shared Data",
  updateSD: "Update Shared Data",
  createSD: "Create Shared Data",
});

export const productListSlice = createSlice({
  name: "productList",
  initialState: {
    items: [],
    paginationTokens: [],
    currentPage: 0,
    limit: 10,
    filter: {},
    preDefinedFilterValues: {},
    total: null,
  },
  reducers: {
    fetchList: (state, action) => {
      const { payload } = action;
      state.items = payload.items;
      if(payload.nextToken) {
        state.total = (state.currentPage * state.items) + 10
      }
      state.total = payload.total;
      if (payload.initLoad) {
        state.paginationTokens = [null, payload.nextToken];
        state.currentPage = 1;
      } else {
        state.paginationTokens = [...state.paginationTokens, payload.nextToken];
      }
      state.limit = payload.limit;
    },
    addProduct: (state, action) => {
      const { payload } = action;
      state.items = [...state.items, payload.data];
    },
    updateProduct: (state, action) => {
      const { payload } = action;
      const idxToBeUpdated = state.items.findIndex(
        (item) => item.ModelNumber === payload.data.ModelNumber
      );
      state.items[idxToBeUpdated] = payload.data;
    },
    incrementPage: (state) => {
      state.currentPage = state.currentPage + 1;
    },
    decrementPage: (state) => {
      state.currentPage = state.currentPage - 1;
    },
    setFilter: (state, action) => {
      const { payload } = action;
      state.filter = payload;
    },
    setPredefinedFilterValues: (state, action) => {
      const { payload } = action;
      state.preDefinedFilterValues = payload;
    },
  },
});

export const getProductList = (payload) => async (dispatch, getState) => {
  const currState = getState().productList;
  const updatedPayload = {
    ...payload,
    filter: currState.filter,
  };
  dispatch(setLoading({ isLoading: true }));
  await getProducts(updatedPayload, dispatch);
  dispatch(setLoading({ isLoading: false }));
};

export const updatePage = (payload) => async (dispatch, getState) => {
  const currState = getState().productList;
  const { currentPage, paginationTokens, limit } = currState;
  const { gotoPage, sortPayload } = payload;
  const updatedPayload = {
    filter: currState.filter,
    nextToken: paginationTokens[gotoPage - 1],
    initLoad: false,
    limit,
  };

  if(sortPayload) {
    updatedPayload.sort = sortPayload
  }

  if (gotoPage > currentPage && !updatedPayload.nextToken) return;
  try {
    dispatch(setLoading({ isLoading: true }));
    await getProducts(updatedPayload, dispatch);
    if (gotoPage > currentPage) {
      dispatch(productListSlice.actions.incrementPage());
    } else {
      dispatch(productListSlice.actions.decrementPage());
    }
    dispatch(setLoading({ isLoading: false }));
  } catch (e) {
    dispatch(setLoading({ isLoading: false }));
    throw e;
  }
};

export const searchProduct = (payload) => async (dispatch, getState) => {
  const currState = getState().productList;
  // Support both calling conventions: payload can be the filter object or { filter, sort }
  const filterObj = payload && payload.hasOwnProperty('filter') ? payload.filter : payload;
  const updatedPayload = {
    filter: filterObj,
    initLoad: true,
    limit: currState.limit,
  };
  if (payload && payload.sort) {
    updatedPayload.sort = payload.sort;
  }
  dispatch(productListSlice.actions.setFilter(filterObj));
  try {
    dispatch(setLoading({ isLoading: true }));
    await getProducts(updatedPayload, dispatch);
    dispatch(setLoading({ isLoading: false }));
  } catch (e) {
    dispatch(setLoading({ isLoading: false }));
    throw e;
  }
};

export const updatePreDefinedFilterValues =
  (payload) => async (dispatch, getState) => {
    try {
      dispatch(setLoading({ isLoading: true }));
      const filter = await productApi.getProductFilterFieldList();
      dispatch(productListSlice.actions.setPredefinedFilterValues(filter));
      dispatch(setLoading({ isLoading: false }));
    } catch (e) {
      dispatch(setLoading({ isLoading: false }));
      throw e;
    }
  };

const getProducts = async (payload, dispatch) => {
  try {
    const params = { limit: payload.limit || 10 };
    if (payload.nextToken) {
      params.nextToken = payload.nextToken;
    }
    params.filter = payload.filter;

    if(payload.sort) {
      params.sort = payload.sort;
    }

    const res = await productApi.searchProducts(params);
    res.initLoad = !!(payload.initLoad && payload.initLoad);
    if (res.items.length > 0) {
      dispatch(
        productListSlice.actions.fetchList({
          ...res,
          limit: params.limit || 10,
        })
      );
    } else {
      dispatch(
        setToast({
          isOpen: true,
          message: "No result found!",
          onDismiss: () => dispatch(setToast({ isOpen: false })),
        })
      );
    }
  } catch (e) {
    throw e;
  }
};

export const updateProduct = (payload) => async (dispatch) => {
  const updatedProduct = { ...payload.updatedProduct };
  delete updatedProduct.createdAt;
  delete updatedProduct.updatedAt;
  if (payload.updatedProduct.hasOwnProperty("Recipe")) {
    updatedProduct.Recipe = payload.updatedProduct.Recipe.Name;
    updatedProduct.RecipeDescription =
      payload.updatedProduct.Recipe.Description;
  }

  try {
    dispatch(setLoading({ isLoading: true }));
    const data = await productApi.updateProduct({ updatedProduct });
    dispatch(productListSlice.actions.updateProduct({ data }));
    dispatch(setLoading({ isLoading: false }));
    dispatch(
      setToast({
        isOpen: true,
        message: "The Product has been successfully updated.",
        onDismiss: () => dispatch(setToast({ isOpen: false })),
      })
    );
  } catch (e) {
    dispatch(setLoading({ isLoading: false }));
    throw e;
  }
};

export const addProduct = (payload) => async (dispatch, getState) => {
  const currState = getState().productList;
  const newProduct = { ...payload.product };
  newProduct.SdAvailable = false;
  newProduct.Recipe = payload.product.Recipe.Name;
  newProduct.RecipeDescription = payload.product.Recipe.Description;
  newProduct.ProductionVersion = 0;
  newProduct.StagingVersion = 0;
  newProduct.State = null;
  newProduct.CoolCloudDBAck = false; 

  try {
    dispatch(setLoading({ isLoading: true }));
    const res = await productApi.listProducts({
      filter: { ModelNumber: { eq: newProduct.ModelNumber } },
    });
    dispatch(setLoading({ isLoading: false }));
    if (res.items.length > 0) {
      dispatch(
        setToast({
          isOpen: true,
          message:
            "Cannot create the product. The Product with the same Model Number already exist.",
          onDismiss: () => dispatch(setToast({ isOpen: false })),
        })
      );
      return;
    }
  } catch (e) {
    dispatch(setLoading({ isLoading: false }));
    throw e;
  }

  try {
    dispatch(setLoading({ isLoading: true }));
    const createdProduct = await productApi.createProduct({ newProduct });
   if(Object.keys(createdProduct)){
    setTimeout(async ()=>{  
        await dispatch(getProductList({ initLoad: true, limit: currState.limit}));
      dispatch(setLoading({ isLoading: false }));
      dispatch(
        setToast({
          isOpen: true,
          message: "The Product has been successfully created.",
          onDismiss: () => dispatch(setToast({ isOpen: false })),
        })
      );
    }
    ,3000)
  }
  } catch (e) {
    dispatch(setLoading({ isLoading: false }));
    throw e;
  }
};

export const selectProductList = (state) => state.productList.items;
export const selectLimit = (state) => state.productList.limit;
export const selectCurrentPage = (state) => state.productList.currentPage;
export const selectTotalResults = (state) => state.productList.total;
export const selectFilter = (state) => state.productList.filter;
export const selectPredefinedFilterValues = (state) =>
  state.productList.preDefinedFilterValues;

export const { setFilter, setSort, setSearchText } = productListSlice.actions;
export default productListSlice.reducer;
